import type {
  PublicTournamentBracket,
  PublicTournamentBracketStage,
} from "@games-monorepo/yak-trails-core";
import { useEffect, useMemo, useState } from "react";

interface TournamentBracketProps {
  bracket: PublicTournamentBracket;
  currentPlayerId?: string;
}

function stageStatus(stage: PublicTournamentBracketStage, now: number): string {
  if (now < stage.startsAtMs) return "UP NEXT";
  if (now <= stage.endsAtMs) return "LIVE";
  return "COMPLETE";
}

export function TournamentBracket({ bracket, currentPlayerId }: TournamentBracketProps) {
  const stages = useMemo(
    () => [...bracket.stages].sort((left, right) => left.order - right.order),
    [bracket.stages],
  );
  const [selectedStageId, setSelectedStageId] = useState(stages[0]?.id ?? "");
  useEffect(() => {
    if (!stages.some((stage) => stage.id === selectedStageId)) {
      setSelectedStageId(stages[0]?.id ?? "");
    }
  }, [selectedStageId, stages]);
  const selected = stages.find((stage) => stage.id === selectedStageId) ?? stages[0];
  const now = Date.now();

  if (!selected) {
    return <div className="yak-trails__bracket-empty">The bracket is waiting for its first stage.</div>;
  }

  return (
    <section className="yak-trails__bracket" aria-label="Tournament bracket">
      <div className="yak-trails__bracket-progress" role="tablist" aria-label="Tournament stages">
        {stages.map((stage, index) => (
          <button
            type="button"
            role="tab"
            aria-selected={stage.id === selected.id}
            className={stage.id === selected.id ? "is-selected" : ""}
            key={stage.id}
            onClick={() => setSelectedStageId(stage.id)}
          >
            <span>{index + 1}</span>
            <b>{stage.name}</b>
            <small>{stageStatus(stage, now)}</small>
          </button>
        ))}
      </div>

      <header className="yak-trails__bracket-stage-header">
        <div>
          <span>{stageStatus(selected, now)}</span>
          <h4>{selected.name}</h4>
        </div>
        <small>
          {selected.respawnPolicy === "elimination"
            ? "One life"
            : selected.respawnPolicy === "immediate"
              ? "Instant respawns"
              : "Timed respawns"}
          {" • "}{selected.mode === "team" ? "Red vs Blue" : "FFA"}
        </small>
      </header>

      {selected.heats.length === 0 ? (
        <div className="yak-trails__bracket-empty">
          <strong>Seeds are forming</strong>
          <span>{selected.waitingEntrants} checked-in player{selected.waitingEntrants === 1 ? "" : "s"} waiting for allocation.</span>
        </div>
      ) : (
        <div className="yak-trails__heat-grid">
          {selected.heats.map((heat) => (
            <article className={`yak-trails__heat-card is-${heat.status}`} key={heat.id}>
              <header>
                <span>HEAT {heat.index + 1}</span>
                <strong>{heat.status.toUpperCase()}</strong>
              </header>
              <div className="yak-trails__heat-entrants">
                {heat.entrants.map((entrant, index) => (
                  <div
                    className={entrant.userId === currentPlayerId ? "is-you" : ""}
                    key={entrant.userId}
                  >
                    <span className={`yak-trails__team-seed ${entrant.teamId ? `is-${entrant.teamId}` : ""}`}>
                      {entrant.placement ?? index + 1}
                    </span>
                    <b>{entrant.alias}{entrant.userId === currentPlayerId ? " (You)" : ""}</b>
                    <small>
                      {entrant.isBye
                        ? "BYE"
                        : entrant.resultRecorded
                          ? `${(entrant.score ?? 0).toFixed(1)} pts`
                          : heat.status === "live" ? "PLAYING" : `SEED ${entrant.seed ?? index + 1}`}
                    </small>
                  </div>
                ))}
              </div>
            </article>
          ))}
          {selected.hiddenHeatCount > 0 && (
            <div className="yak-trails__bracket-empty">
              <strong>+{selected.hiddenHeatCount} more heats</strong>
              <span>Your own heat is always kept in view.</span>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
